import { useCallback, useEffect, useId, useRef, useState, type CSSProperties } from 'react';

import { TagChip } from '../../atoms/TagChip';
import { TruncatedCellText } from '../../atoms/TruncatedCellText';
import { cn } from '../../lib/utils';

/**
 * Anchor side for the popover panel relative to the `+N` chip.
 */
export type TopicListPopoverPlacement = 'bottom-start' | 'bottom-end' | 'top-start' | 'top-end';

/**
 * Open mechanism. Today only `'hover'` (CSS group-hover/focus-within) is
 * implemented; a future click-toggle variant can extend this union.
 */
export type TopicListPopoverTrigger = 'hover';

export interface TopicListPopoverProps {
  /** Primary topic shown as the visible label in the cell. */
  primary: string;
  /** Topics hidden behind the `+N` chip. Length drives chip rendering: 0 = chip + popover omitted. */
  additional: readonly string[];
  /**
   * Header text for the popover (caller owns i18n).
   * Receives `totalCount` = `additional.length + 1` (primary counted in).
   * Default at the caller layer (`AccessLegitimacyBody`) is the English
   * template `(n) => "Document Topic (" + n + ")"`.
   */
  popoverTitle: (totalCount: number) => string;
  /**
   * Accessible label applied to the `+N` chip.
   * Receives `additionalCount` = `additional.length`.
   * Default at the caller layer is `(n) => "plus " + n + " more topics"`.
   */
  chipAriaLabel: (additionalCount: number) => string;
  /** Popover anchor side. Default: `'bottom-start'` (opens below the chip, left-aligned). */
  placement?: TopicListPopoverPlacement;
  /** Open mechanism. Default: `'hover'` (CSS group-hover/focus-within — current behavior). */
  trigger?: TopicListPopoverTrigger;
  className?: string;
}

// The chip↔panel gap is *padding on the panel's own box* (see the JSX), not a
// margin: the pointer can cross it from any angle without leaving the hover
// group, because the panel box always spans the chip's x-range. The position
// itself is computed from the anchor rect (see `reposition`).
const PLACEMENT_CLASSES = {
  'bottom-start': 'pt-1',
  'bottom-end': 'pt-1',
  'top-start': 'pb-1',
  'top-end': 'pb-1',
} as const satisfies Readonly<Record<TopicListPopoverPlacement, string>>;

/** Flip a `bottom-*` placement to its `top-*` twin (and vice versa). */
const FLIPPED_PLACEMENT = {
  'bottom-start': 'top-start',
  'bottom-end': 'top-end',
  'top-start': 'bottom-start',
  'top-end': 'bottom-end',
} as const satisfies Readonly<Record<TopicListPopoverPlacement, TopicListPopoverPlacement>>;

// Estimated panel height (header + padding + per-topic row), used only on the
// first frame of an open, while the panel is still display:none and measures 0;
// every later frame uses the real height.
const POPOVER_BASE_PX = 45;
const POPOVER_ROW_PX = 20;

/** The five inline properties `reposition` writes; equal → skip the state update. */
const sameStyle = (a: CSSProperties, b: CSSProperties): boolean =>
  a.top === b.top &&
  a.bottom === b.bottom &&
  a.left === b.left &&
  a.right === b.right &&
  a.display === b.display;

/**
 * `TopicListPopover` — primary topic + optional `+N` chip + hover/focus
 * popover listing all topics. Extracted from the inline JSX previously in
 * `AccessLegitimacyGrid` documentTopic column.
 *
 * Strings + placement + trigger are caller-owned via props. Default
 * `placement='bottom-start'` + `trigger='hover'` reproduces the legacy chrome
 * bit-for-bit: a `group/topic-popover` wrapper with a tab-stop chip
 * (`tabIndex=0`, `cursor-default`, `outline-none`) and a popover panel that
 * opens via `group-hover/topic-popover:flex` and
 * `group-focus-within/topic-popover:flex`.
 *
 * The panel is `position: fixed`, placed from the anchor's viewport rect on
 * every open (TDEV-960). The grid that hosts this molecule scrolls its rows in
 * an `overflow` region, and an absolutely-positioned panel on a row near that
 * region's edge was clipped by it; a fixed panel escapes every overflow
 * ancestor, so the viewport is the only boundary the flip check has to respect.
 *
 * Per design Q1: this molecule is intentionally topic-specific. A generic
 * `ChipPopoverList` is deferred until a 2nd consumer surfaces. The strings
 * are caller-supplied so i18n adapters remain at the wrapper layer.
 *
 * `data-testid="topic-additional-chip"` + `data-testid="topic-additional-popover"`
 * + `data-testid="cell-document-topic-primary"` are preserved verbatim from the
 * pre-extraction inline JSX so `AccessLegitimacyGrid.test.tsx` queries flow
 * through unmodified. Phase 4 (TDEV-086): the primary label is now rendered via
 * `TruncatedCellText` (AC6 closure) — `data-testid="cell-document-topic-primary"`
 * is forwarded to the `TruncatedCellText` root `<span>`.
 *
 * Used by `AccessLegitimacyGrid` via `AccessLegitimacyBody`.
 */
export function TopicListPopover({
  primary,
  additional,
  popoverTitle,
  chipAriaLabel,
  placement = 'bottom-start',
  trigger: _trigger = 'hover',
  className,
}: TopicListPopoverProps) {
  const popoverId = useId();
  const additionalCount = additional.length;
  const allTopics = [primary, ...additional];

  // Viewport-collision flip + fixed placement: the popover is pure CSS
  // (hover/focus display toggle), so before it shows we check whether a
  // downward panel would cross the fold (or an upward one the top edge), flip
  // the vertical side if so, and pin the fixed panel to the anchor's edges.
  // Runs on open and then once per frame while open (see below), so the panel
  // follows a scrolling, resizing or re-laid-out anchor and hides when
  // something covers it.
  // ponytail: `window.inner*` ignores a document scrollbar; swap for
  // `documentElement.client*` if a `*-end` panel ever sits under one.
  const anchorRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [effectivePlacement, setEffectivePlacement] = useState(placement);
  const [panelStyle, setPanelStyle] = useState<CSSProperties>({});
  const topicCount = allTopics.length;
  const reposition = useCallback(() => {
    const anchor = anchorRef.current;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    // A fixed panel is not clipped along with its row, so hide it while the
    // anchor itself is not visible — scrolled under the sticky header, past the
    // region's edge, or covered by an overlay. Whatever is painted at the
    // anchor's centre decides — except the panel itself: a clamped panel can
    // overlap its own chip, and being a DOM descendant of the anchor it would
    // vouch for a chip that is really under the sticky header. Skip its
    // elements and judge the first one that remains.
    // ponytail: jsdom has no hit-testing (`elementsFromPoint` is undefined) —
    // treat that as visible.
    const hitTest = document.elementsFromPoint as typeof document.elementsFromPoint | undefined;
    const hits = hitTest
      ? hitTest.call(document, rect.left + rect.width / 2, rect.top + rect.height / 2)
      : undefined;
    const panel = panelRef.current;
    const firstOutsidePanel = hits?.find((el) => panel === null || !panel.contains(el));
    const anchorVisible =
      hits === undefined || (firstOutsidePanel !== undefined && anchor.contains(firstOutsidePanel));
    // Real size once open; the estimate only on the first frame (display:none → 0).
    const panelRect = panelRef.current?.getBoundingClientRect();
    const panelHeight = panelRect?.height || POPOVER_BASE_PX + topicCount * POPOVER_ROW_PX;
    const panelWidth = panelRect?.width ?? 0;
    // Vertical side: keep the requested side while it fits, flip when only the
    // other side fits, and when neither fits take the roomier side — the clamp
    // below then pins the panel inside the viewport instead of past it.
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const wantsDown = placement.startsWith('bottom');
    const fitsWanted = panelHeight <= (wantsDown ? spaceBelow : spaceAbove);
    const fitsFlipped = panelHeight <= (wantsDown ? spaceAbove : spaceBelow);
    const roomierIsWanted = spaceBelow >= spaceAbove === wantsDown;
    const next =
      fitsWanted || (!fitsFlipped && roomierIsWanted) ? placement : FLIPPED_PLACEMENT[placement];
    setEffectivePlacement(next);
    // Collision clamp on both axes: labels wrap only past 100vw and the body
    // scrolls past 100vh, so the panel always fits and this keeps it on-screen.
    const clamp = (offset: number, size: number, viewport: number): number =>
      Math.max(0, Math.min(offset, Math.max(0, viewport - size)));
    const nextStyle: CSSProperties = {
      ...(anchorVisible ? {} : { display: 'none' }),
      ...(next.startsWith('bottom')
        ? { top: clamp(rect.bottom, panelHeight, window.innerHeight) }
        : { bottom: clamp(window.innerHeight - rect.top, panelHeight, window.innerHeight) }),
      ...(next.endsWith('start')
        ? { left: clamp(rect.left, panelWidth, window.innerWidth) }
        : { right: clamp(window.innerWidth - rect.right, panelWidth, window.innerWidth) }),
    };
    // Called every frame while open: keep the previous object when nothing
    // moved so idle frames do not re-render.
    setPanelStyle((prev) => (sameStyle(prev, nextStyle) ? prev : nextStyle));
  }, [placement, topicCount]);

  // Open = hovered or focused, mirroring the CSS `group-hover` / `group-focus-within`
  // toggle. While open, re-evaluate once per animation frame (Floating UI's
  // `autoUpdate({ animationFrame: true })` idea) instead of enumerating the
  // events that can move or cover the anchor — grid scroll, window resize, a
  // refetch overlay, the sticky header sliding over the row all look the same
  // to a per-frame rect read + hit-test. One open panel at a time, and
  // `reposition` only sets state when a value changes, so the loop is cheap.
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const open = hovered || focused;
  useEffect(() => {
    if (!open) return;
    let frame = requestAnimationFrame(function tick() {
      reposition();
      frame = requestAnimationFrame(tick);
    });
    return () => cancelAnimationFrame(frame);
  }, [open, reposition]);

  return (
    <div className={cn('flex min-w-0 items-center gap-1', className)}>
      <TruncatedCellText data-testid="cell-document-topic-primary" text={primary} />
      {additionalCount > 0 ? (
        <div
          ref={anchorRef}
          className="group/topic-popover relative shrink-0"
          onMouseEnter={() => {
            setHovered(true);
            reposition();
          }}
          onMouseLeave={() => setHovered(false)}
          onFocus={() => {
            setFocused(true);
            reposition();
          }}
          onBlur={() => setFocused(false)}
        >
          <span
            data-testid="topic-additional-chip"
            aria-label={chipAriaLabel(additionalCount)}
            aria-describedby={popoverId}
            tabIndex={0}
            className="block cursor-default outline-none focus-visible:ring-2 focus-visible:ring-dawn-orange focus-visible:ring-offset-1"
          >
            <TagChip size="sm" state="subtle">
              {`+${additionalCount}`}
            </TagChip>
          </span>
          <div
            ref={panelRef}
            id={popoverId}
            data-testid="topic-additional-popover"
            role="tooltip"
            style={panelStyle}
            className={cn(
              // Outer box = hit area. Its padding is the 4px gap to the chip, so
              // the pointer crosses the gap without leaving the hover group (the
              // panel takes pointer events: hovering it keeps it open and the
              // wheel reaches the inner scroller). Capped at the viewport on both
              // axes: an over-wide label wraps, an over-tall list scrolls inside.
              'fixed z-30 hidden max-h-[100vh] max-w-[100vw] flex-col',
              PLACEMENT_CLASSES[effectivePlacement],
              'group-hover/topic-popover:flex group-focus-within/topic-popover:flex'
            )}
          >
            <div className="flex min-h-0 flex-col gap-2 overflow-y-auto rounded-[4px] border border-grey-200 bg-dawn-white p-3 shadow-md">
              <p className="font-outfit text-[11px] leading-[1.4] text-grey-500">
                {popoverTitle(allTopics.length)}
              </p>
              {allTopics.map((topic, i) => (
                <p
                  key={`${i}-${topic}`}
                  className="font-outfit text-[10px] capitalize leading-[1.2] text-dawn-black"
                >
                  {topic}
                </p>
              ))}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

TopicListPopover.displayName = 'TopicListPopover';
